import { Check, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';
import { getThemeList, DEFAULT_THEME_ID, type ThemeConfig } from '../../config/themes';
import DisplayModeToggle from '../common/DisplayModeToggle';

const layoutLabels: Record<string, Record<string, string>> = {
  menuStructure: { sidebar: 'Sidebar', topnav: 'Nav horizontale', rail: 'Rail icônes' },
  cardStyle: { bordered: 'Bordures', shadow: 'Ombres', glass: 'Glass' },
  buttonShape: { rounded: 'Arrondi', pill: 'Pilule', square: 'Angulaire' },
  inputStyle: { bordered: 'Bordé', underline: 'Souligné', filled: 'Rempli' },
  navStyle: { highlight: 'Fond', pill: 'Pilule', indicator: 'Barre' },
  sidebarStyle: { solid: 'Classique', colored: 'Sombre', transparent: 'Verre' },
  headerStyle: { standard: 'Standard', accent: 'Couleur', transparent: 'Verre' },
  density: { compact: 'Compact', comfortable: 'Normal', spacious: 'Aéré' },
  pageBackground: { flat: 'Uni', gradient: 'Dégradé', mesh: 'Mesh' },
};

function LayoutTag({ category, value }: { category: string; value: string }) {
  const label = layoutLabels[category]?.[value] || value;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-medium">
      {label}
    </span>
  );
}

function renderMiniCards(theme: ThemeConfig, layout: ThemeConfig['layout'], colors: ThemeConfig['colors']) {
  return [0, 1].map((i) => {
    const s: React.CSSProperties = { borderRadius: theme.style.borderRadius, padding: '4px' };
    if (layout.cardStyle === 'bordered') { s.backgroundColor = colors['bg-surface']; s.border = `1px solid ${colors['border-light']}`; }
    else if (layout.cardStyle === 'shadow') { s.backgroundColor = colors['bg-surface']; s.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'; }
    else { s.backgroundColor = 'rgba(255,255,255,0.6)'; s.border = '1px solid rgba(255,255,255,0.4)'; }
    const iconR = layout.iconShape === 'circle' ? '50%' : layout.iconShape === 'square' ? '2px' : '3px';
    return (
      <div key={i} className="flex-1" style={s}>
        <div className="flex items-center gap-1 mb-1">
          <div style={{ width: '8px', height: '8px', borderRadius: iconR, backgroundColor: colors['primary-400'] }} />
          <div style={{ height: '4px', width: '60%', borderRadius: '2px', backgroundColor: colors['primary-200'] }} />
        </div>
        <div style={{ height: '3px', width: '45%', borderRadius: '2px', backgroundColor: colors['border-default'], marginLeft: '12px' }} />
      </div>
    );
  });
}

function ThemePreview({ theme, isActive, onSelect, defaultLabel }: {
  theme: ThemeConfig;
  isActive: boolean;
  onSelect: () => void;
  defaultLabel: string;
}) {
  const colors = theme.colors;
  const { layout } = theme;

  // Determine sidebar background for preview
  const sidebarBg = layout.sidebarStyle === 'colored'
    ? (colors['sidebar-bg'] || '#1e0a3c')
    : layout.sidebarStyle === 'transparent'
      ? 'rgba(255,255,255,0.45)'
      : '#ffffff';

  const sidebarBorder = layout.sidebarStyle === 'solid' ? '1px solid #e5e7eb' : 'none';

  // Determine header background for preview
  const headerBg = layout.headerStyle === 'accent'
    ? (colors['header-bg'] || '#1e0a3c')
    : layout.headerStyle === 'transparent'
      ? 'rgba(255,255,255,0.5)'
      : '#ffffff';

  const headerBorder = layout.headerStyle === 'standard' ? '1px solid #e5e7eb' : 'none';

  // Page background
  const pageBg = layout.pageBackground === 'gradient'
    ? `linear-gradient(135deg, ${colors['page-gradient-from'] || colors['primary-50']} 0%, ${colors['page-gradient-to'] || colors['bg-base']} 100%)`
    : layout.pageBackground === 'mesh'
      ? `radial-gradient(at 30% 30%, ${colors['primary-100']}40 0px, transparent 60%), ${colors['bg-base']}`
      : colors['bg-base'];

  return (
    <button
      onClick={onSelect}
      className={`relative w-full text-left rounded-xl border-2 p-4 transition-all duration-200 hover:shadow-md ${
        isActive
          ? 'border-primary-500 shadow-md ring-2 ring-primary-200'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      {isActive && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-primary-500 text-white flex items-center justify-center">
          <Check size={14} />
        </div>
      )}

      <div className="mb-3">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold text-gray-900 text-base">{theme.name}</h4>
          {theme.id === DEFAULT_THEME_ID && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-medium">
              {defaultLabel}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-0.5">{theme.description}</p>
      </div>

      {/* Color swatches */}
      <div className="flex gap-1.5 mb-3">
        {[
          colors['primary-300'],
          colors['primary-500'],
          colors['primary-700'],
          colors['secondary-300'],
          colors['secondary-500'],
        ].map((color, i) => (
          <div
            key={i}
            className="w-6 h-6 rounded-lg"
            style={{ backgroundColor: color }}
          />
        ))}
      </div>

      {/* Full layout mini-preview — structure varies per menuStructure */}
      <div className="rounded-lg overflow-hidden border border-gray-200 mb-3" style={{ height: '100px' }}>
        {layout.menuStructure === 'topnav' ? (
          /* TOPNAV preview: header with nav tabs, no sidebar */
          <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-2 shrink-0" style={{ height: '22px', backgroundColor: headerBg, borderBottom: headerBorder }}>
              <div style={{ width: '16px', height: '6px', borderRadius: '2px', backgroundColor: colors['primary-500'] }} />
              {[0, 1, 2].map(i => (
                <div key={i} style={{
                  width: `${20 - i * 3}px`, height: '5px', borderRadius: '8px',
                  backgroundColor: i === 0 ? 'rgba(255,255,255,0.5)' : (layout.headerStyle === 'transparent' ? '#d1d5db' : 'rgba(255,255,255,0.2)'),
                }} />
              ))}
              <div className="flex-1" />
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: layout.headerStyle === 'accent' ? 'rgba(255,255,255,0.3)' : colors['primary-200'] }} />
            </div>
            <div className="flex-1 p-2 flex gap-1.5" style={{ background: pageBg }}>
              {renderMiniCards(theme, layout, colors)}
            </div>
          </div>
        ) : layout.menuStructure === 'rail' ? (
          /* RAIL preview: narrow icon strip + accent header */
          <div className="flex h-full">
            <div className="w-6 flex flex-col items-center gap-1 py-1.5 shrink-0 bg-white border-r border-gray-200">
              {[0, 1, 2, 3].map(i => (
                <div key={i} style={{
                  width: '10px', height: '10px',
                  borderRadius: '2px',
                  backgroundColor: i === 0 ? colors['primary-100'] : '#f3f4f6',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{ width: '5px', height: '5px', borderRadius: '1px', backgroundColor: i === 0 ? colors['primary-500'] : '#d1d5db' }} />
                </div>
              ))}
            </div>
            <div className="flex-1 flex flex-col">
              <div className="h-4 flex items-center px-2 shrink-0" style={{ backgroundColor: headerBg, borderBottom: headerBorder }}>
                <div style={{ width: '12px', height: '3px', borderRadius: '1px', backgroundColor: layout.headerStyle === 'accent' ? 'rgba(255,255,255,0.5)' : colors['primary-300'] }} />
                <div style={{ width: '3px', height: '3px', margin: '0 3px', borderRadius: '50%', backgroundColor: layout.headerStyle === 'accent' ? 'rgba(255,255,255,0.2)' : '#d1d5db' }} />
                <div style={{ width: '18px', height: '3px', borderRadius: '1px', backgroundColor: layout.headerStyle === 'accent' ? 'rgba(255,255,255,0.3)' : '#d1d5db' }} />
              </div>
              <div className="flex-1 p-1.5 flex gap-1" style={{ background: pageBg }}>
                {renderMiniCards(theme, layout, colors)}
              </div>
            </div>
          </div>
        ) : (
          /* SIDEBAR preview: traditional left sidebar */
          <div className="flex h-full">
            <div className="w-14 flex flex-col p-1 gap-0.5 shrink-0" style={{ backgroundColor: sidebarBg, borderRight: sidebarBorder }}>
              {[0, 1, 2, 3].map(i => {
                const active = i === 0;
                const navColor = layout.sidebarStyle === 'colored' ? 'rgba(255,255,255,0.7)' : colors['primary-500'];
                const navBg = layout.sidebarStyle === 'colored' ? (colors['sidebar-active-bg'] || 'rgba(168,85,247,0.2)') : colors['primary-100'];
                const inact = layout.sidebarStyle === 'colored' ? 'rgba(255,255,255,0.2)' : '#d1d5db';
                const s: React.CSSProperties = { height: '12px', margin: '1px 2px', display: 'flex', alignItems: 'center' };
                if (active) {
                  if (layout.navStyle === 'pill') { s.backgroundColor = navBg; s.borderRadius = '9999px'; }
                  else if (layout.navStyle === 'indicator') { s.borderLeft = `2px solid ${navColor}`; s.paddingLeft = '4px'; }
                  else { s.backgroundColor = navBg; s.borderRadius = '4px'; }
                }
                return (
                  <div key={i} style={s}>
                    <div style={{ width: `${55 - i * 6}%`, height: '5px', borderRadius: '2px', backgroundColor: active ? navColor : inact, marginLeft: layout.navStyle === 'indicator' && !active ? '6px' : '4px' }} />
                  </div>
                );
              })}
            </div>
            <div className="flex-1 flex flex-col">
              <div className="h-5 flex items-center justify-end px-2 shrink-0" style={{ backgroundColor: headerBg, borderBottom: headerBorder }}>
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: layout.headerStyle === 'accent' ? 'rgba(255,255,255,0.3)' : colors['primary-200'] }} />
              </div>
              <div className="flex-1 p-1.5 flex gap-1.5" style={{ background: pageBg }}>
                {renderMiniCards(theme, layout, colors)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Layout tags */}
      <div className="flex flex-wrap gap-1">
        <LayoutTag category="menuStructure" value={layout.menuStructure} />
        <LayoutTag category="cardStyle" value={layout.cardStyle} />
        <LayoutTag category="buttonShape" value={layout.buttonShape} />
        <LayoutTag category="inputStyle" value={layout.inputStyle} />
        <LayoutTag category="density" value={layout.density} />
        <LayoutTag category="pageBackground" value={layout.pageBackground} />
      </div>
    </button>
  );
}

export default function ThemeSwitcher() {
  const { t } = useTranslation('settings');
  const { themeId, setTheme } = useThemeStore();
  const themes = getThemeList();
  const defaultLabel = t('appearance.defaultBadge');

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-primary-50">
          <Palette size={20} className="text-primary-600" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900">Thème de l'application</h3>
          <p className="text-sm text-gray-500">
            Chaque thème change complètement l'apparence : couleurs, formes, disposition
          </p>
        </div>
      </div>

      <div className="mb-6">
        <DisplayModeToggle />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {themes.map((theme) => (
          <ThemePreview
            key={theme.id}
            theme={theme}
            isActive={themeId === theme.id}
            onSelect={() => setTheme(theme.id)}
            defaultLabel={defaultLabel}
          />
        ))}
      </div>
    </div>
  );
}
